import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Popconfirm, Result, Space, Spin, Tabs, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import tmService from '../../services/tmService';
import { releaseSandbox } from './Streams';
import { SandboxTag, StreamStatusTag, StreamTypeTag, toastErr } from './components/common';
import Overview from './components/Overview';
import ApiKeys from './components/ApiKeys';
import Domains from './components/Domains';
import Messages from './components/Messages';
import Suppressions from './components/Suppressions';
import Webhook from './components/Webhook';
import Marketing from './components/Marketing';
import Recipients from './components/Recipients';
import Settings from './components/Settings';

const StreamDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [stream, setStream] = useState(null);
    const [loading, setLoading] = useState(true);
    const [missing, setMissing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmService.getStream(id);
            setStream(r?.data || null);
            setMissing(false);
        } catch (e) {
            if (e?.status === 404) setMissing(true);
            else toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    if (missing) {
        return <Result status="404" title={t('Stream not found')}
            extra={<Button onClick={() => navigate(userPath('/tm/streams'))}>{t('Back to list')}</Button>} />;
    }
    if (!stream) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

    const items = [
        { key: 'overview', label: t('Overview'), children: <Overview stream={stream} /> },
        { key: 'keys', label: t('API key'), children: <ApiKeys stream={stream} onChange={load} /> },
        { key: 'domains', label: t('Sending domains'), children: <Domains stream={stream} onChange={load} /> },
        { key: 'messages', label: t('Message log'), children: <Messages stream={stream} /> },
        { key: 'suppressions', label: t('Suppression list'), children: <Suppressions stream={stream} /> },
        { key: 'webhook', label: 'Webhook', children: <Webhook stream={stream} /> },
        stream.type === 'marketing' && { key: 'marketing', label: t('Marketing settings'), children: <Marketing stream={stream} onSaved={setStream} /> },
        stream.sandbox && { key: 'recipients', label: t('Sandbox recipients'), children: <Recipients /> },
        { key: 'settings', label: t('Settings'), children: <Settings stream={stream} onSaved={setStream} /> },
    ].filter(Boolean);
    const tab = items.some((i) => i.key === params.get('tab')) ? params.get('tab') : 'overview';

    return (
        <div>
            <PageHeader
                title={stream.name}
                onBack={() => navigate(userPath('/tm/streams'))}
                tags={
                    <Space size={4} wrap>
                        <StreamTypeTag type={stream.type} />
                        <StreamStatusTag stream={stream} />
                        {stream.sandbox && <SandboxTag />}
                    </Space>
                }
                extra={
                    <Space>
                        {stream.sandbox_releasable && stream.status === 'active' && (
                            <Button onClick={() => releaseSandbox(stream, t, load)}>{t('Leave sandbox')}</Button>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                        <Popconfirm title={t('Delete this stream?')} description={t('Sending stops now, keys stop working, no refund')}
                            okText={t('Delete')} okButtonProps={{ danger: true }}
                            onConfirm={async () => {
                                try {
                                    await tmService.deleteStream(stream.id);
                                    message.success(t('Deleted'));
                                    navigate(userPath('/tm/streams'));
                                } catch (e) { toastErr(e, t('Deletion failed'), true); }
                            }}>
                            <Button danger>{t('Delete')}</Button>
                        </Popconfirm>
                    </Space>
                }
            />
            {stream.status === 'banned' && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }}
                    message={t('Banned: {{r}}', { r: stream.banned_reason || '-' })} description={t('Please contact support')} />
            )}
            {stream.status === 'paused' && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('Paused: {{r}}', { r: stream.paused_reason || '-' })} />
            )}
            {(stream.needs_postal_address ?? (stream.type === 'marketing' && !stream.postal_address)) && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t('Marketing streams need the sender\'s postal address first')}
                    description={t('Until then, both the API and SMTP reject sends')}
                    action={<Button size="small" onClick={() => setParams({ tab: 'marketing' })}>{t('Fill in')}</Button>} />
            )}
            <Card styles={{ body: { paddingTop: 4 } }}>
                <Tabs activeKey={tab} onChange={(k) => setParams(k === 'overview' ? {} : { tab: k })} items={items} destroyInactiveTabPane />
            </Card>
        </div>
    );
};

export default StreamDetail;
